/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { AnyAction } from '@reduxjs/toolkit';
import * as actionTypes from '../constants/action-types';
import { UserRoles } from '../constants/user';
import { InitiativePlain } from '../types/initiative';
import { AppCode } from '../types/app';
import { GenericState } from '../slice/slice';

export interface Permission {
  initiativeId: string;
  permissions: UserRoles[];
}

interface AuthenticationProvider {
  type: string; // E.g: 'FEDERATION' | 'OKTA'
  name: string;
}

export type InitiativeTreeItem = Pick<InitiativePlain, '_id' | 'parentId' | 'permissionGroup' | 'appConfigCode'>;

export interface CurrentUserData {
  _id: string;
  active: boolean;
  created: string;
  email: string;
  firstName: string;
  isStaff: boolean;
  isSuperUser?: boolean;
  jobTitle: string;
  lastLogin: string;
  name: string;
  organizationId: string;
  permissions: Permission[];
  authenticationProvider?: AuthenticationProvider;
  profile: string;
  stakeholderTypes: string[];
  surname: string;
  telephoneNumber: string;
  title: string;
  registrationData?: {
    organizationName?: string;
    onboardingId?: string;
    onboardingData?: {
      appConfigCode?: AppCode;
      referenceCompany?: string;
      referralCode?: string;
    };
  };
  initiativeTree?: InitiativeTreeItem[];
  redirectConfig?: {
    key: string,
    title: string,
    content: string,
    secondary: string,
    primary: string,
    redirectUrl: string,
  }
}

interface CurrentUserStateLoaded extends GenericState<CurrentUserData> {
  loaded: true;
  lastUpdated?: number;
}

interface CurrentUserStateNotLoaded extends GenericState<Partial<CurrentUserData>> {
  loaded: false;
  lastUpdated?: number;
}

export type CurrentUserState = CurrentUserStateLoaded | CurrentUserStateNotLoaded;

const currentUser = (

  state: (CurrentUserStateLoaded | CurrentUserStateNotLoaded) = {
    data: {},
    loaded: false,
    loading: false,
    errored: false,
    errorMessage: ''
  },

  action: AnyAction

): (CurrentUserStateLoaded | CurrentUserStateNotLoaded) => {

  switch (action.type) {

    case actionTypes.LOADING_CURRENT_USER:
      return { ...state, data: {}, loaded: false, loading: true, errored: false, lastUpdated: Date.now() }
    case actionTypes.ERROR_CURRENT_USER:
      return { ...state, data: {}, loaded: false, loading: false, errored: true, lastUpdated: Date.now(), errorMessage: action.errorMessage }
    case actionTypes.NO_DATA_CURRENT_USER:
      return { ...state, data: {}, loaded: false, loading: false, errored: false, lastUpdated: Date.now() }
    case actionTypes.LOADED_CURRENT_USER:
      return { ...state, data: action.data, loaded: true, loading: false, errored: false, lastUpdated: Date.now() }
    default:
      return state;

  }

}

export default currentUser;
